import { Phone, Mail, MapPin, Clock, Instagram, Navigation, MessageCircle } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/motion";
import { AppointmentForm } from "../components/AppointmentForm";
import { MapSection } from "../components/sections";
import { CLINIC, whatsappLink } from "../lib/constants";

const Contact = () => (
  <>
    <PageHero
      kicker="Contact & Appointments"
      testid="contact-hero"
      lines={[
        <>We're here,</>,
        <>
          <span className="font-serif italic font-semibold text-brand-bright">7 days</span> a week.
        </>,
      ]}
      sub="Call, WhatsApp, email or walk in — iNetra Laser Eye & Advanced Retina Centre, Janak Puri, New Delhi."
    />

    <section className="py-20 sm:py-28" data-testid="contact-details-section">
      <div className="container-x">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: Phone, title: "Call Us", lines: [CLINIC.phoneDisplay],
              action: { label: "Call Now", href: CLINIC.phoneLink, testid: "contact-call-button" },
            },
            {
              icon: MessageCircle, title: "WhatsApp", lines: [CLINIC.phoneDisplay],
              action: { label: "Chat on WhatsApp", href: whatsappLink("Hello iNetra, I would like to book an appointment."), testid: "contact-whatsapp-button", external: true },
            },
            {
              icon: Mail, title: "Email", lines: [CLINIC.email],
              action: { label: "Write to Us", href: CLINIC.emailLink, testid: "contact-email-button" },
            },
            {
              icon: Instagram, title: "Instagram", lines: [`@${CLINIC.instagramHandle}`],
              action: { label: "Follow Us", href: CLINIC.instagramUrl, testid: "contact-instagram-button", external: true },
            },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.07}>
              <div className="card-lift h-full rounded-2xl border border-slate-200 bg-white p-6 flex flex-col" data-testid={`contact-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-brand-mist flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-brand-bright" strokeWidth={1.7} aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-extrabold text-ink">{c.title}</h3>
                {c.lines.map((l) => (
                  <p key={l} className="mt-1 text-sm font-semibold text-slate-600 break-all">{l}</p>
                ))}
                <a
                  href={c.action.href}
                  {...(c.action.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-bright hover:gap-2.5 transition-[gap]"
                  data-testid={c.action.testid}
                >
                  {c.action.label} →
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-2xl border border-slate-200 bg-white p-7 h-full" data-testid="contact-address-card">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua flex items-center gap-2">
                <MapPin className="w-4 h-4" aria-hidden="true" /> Address
              </p>
              <p className="mt-4 text-lg font-extrabold text-ink">{CLINIC.fullName}</p>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                {CLINIC.addressLines.map((l) => (
                  <span key={l} className="block">{l}</span>
                ))}
              </p>
              <p className="mt-2 text-xs text-slate-500">Landmark: {CLINIC.landmark}</p>
              <a href={CLINIC.directionsUrl} target="_blank" rel="noopener noreferrer" className="btn-outline mt-6 !text-[13px]" data-testid="contact-directions-button">
                <Navigation className="w-4 h-4" aria-hidden="true" /> Get Directions
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-ink text-white noise-dark relative overflow-hidden p-7 h-full" data-testid="contact-hours-card">
              <p className="relative text-xs font-bold uppercase tracking-[0.2em] text-aqua-bright flex items-center gap-2">
                <Clock className="w-4 h-4" aria-hidden="true" /> Opening Hours
              </p>
              <ul className="relative mt-5 space-y-4">
                {CLINIC.hours.map((h) => (
                  <li key={h.days} className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                    <span className="text-sm font-semibold text-blue-100">{h.days}</span>
                    <span className="text-sm font-extrabold">{h.time}</span>
                  </li>
                ))}
              </ul>
              <p className="relative mt-5 text-xs font-bold text-aqua-bright uppercase tracking-wider">Open 7 days a week · No regular holiday</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section id="book" className="py-20 sm:py-28 bg-slate-50" data-testid="contact-book-section">
      <div className="container-x max-w-4xl">
        <SectionHead
          center
          chapter="02"
          kicker="Book Appointment"
          title="Request your appointment"
          sub="Share your details and preferred time — our team will call you to confirm. You can also continue instantly on WhatsApp after submitting."
        />
        <Reveal delay={0.15} className="mt-10">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-xl shadow-blue-900/5">
            <AppointmentForm />
          </div>
        </Reveal>
      </div>
    </section>

    <MapSection />
  </>
);

export default Contact;
